/**
 * IndustrialMercury - High-Fidelity Mining Hub & Optical Relay Base
 * Features: Heavily cratered surface, ISRU mining infrastructure,
 * mirror arrays, mass driver systems, and processing facilities
 */

import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useStore } from '../../store/useStore';

interface IndustrialMercuryProps {
  position?: [number, number, number];
  radius?: number;
  sunPosition?: [number, number, number];
  onClick?: () => void;
}

// Simplex-like noise for crater generation
function noise2D(x: number, y: number): number {
  const n = Math.sin(x * 12.9898 + y * 78.233) * 43758.5453;
  return n - Math.floor(n);
}

function fbm(x: number, y: number, octaves: number = 4): number {
  let value = 0;
  let amplitude = 0.5;
  let frequency = 1;

  for (let i = 0; i < octaves; i++) {
    value += amplitude * noise2D(x * frequency, y * frequency);
    amplitude *= 0.5;
    frequency *= 2;
  }

  return value;
}

export function IndustrialMercury({
  position = [0, 0, 12],
  radius = 0.6,
  sunPosition = [-30, 0, 0],
  onClick
}: IndustrialMercuryProps) {
  const mercuryRef = useRef<THREE.Mesh>(null);
  const infrastructureRef = useRef<THREE.Group>(null);
  const { view, isClipMode } = useStore();

  // Calculate orbital relay hub position - on Sun-facing side for clear LOS
  const relayHubPosition = useMemo((): [number, number, number] => {
    const mercuryPos = new THREE.Vector3(...position);
    const sunPos = new THREE.Vector3(...sunPosition);
    // Direction from Mercury TOWARD Sun (where EAHP is)
    const toSun = sunPos.clone().sub(mercuryPos).normalize();
    // Position hub 3.5 units from Mercury center, toward Sun
    const hubPos = toSun.multiplyScalar(3.5);
    hubPos.y += 0.5; // Slight vertical offset
    return [hubPos.x, hubPos.y, hubPos.z];
  }, [position, sunPosition]);

  // Calculate spaceport position - on DARK side (opposite from Sun)
  // This is where mining ships land and launch
  const spaceportPosition = useMemo((): [number, number, number] => {
    const mercuryPos = new THREE.Vector3(...position);
    const sunPos = new THREE.Vector3(...sunPosition);
    // Direction AWAY from Sun (dark side)
    const awayFromSun = mercuryPos.clone().sub(sunPos).normalize();
    // Position spaceport ON the surface (radius * 1.0), not floating above
    const spaceportPos = awayFromSun.multiplyScalar(radius);
    return [spaceportPos.x, spaceportPos.y, spaceportPos.z];
  }, [position, sunPosition, radius]);

  // Calculate polar starship base position - at "north pole" (top of planet)
  // This is where Starships from Earth land - separate from mining operations
  const polarBasePosition = useMemo((): [number, number, number] => {
    // Slightly tilted toward the dark side for better visibility from typical camera angles
    return [radius * 0.2, radius * 0.98, radius * 0.15];
  }, [radius]);

  // Generate HIGH-RESOLUTION procedural surface textures
  const { surfaceTexture, bumpTexture, roughnessTexture } = useMemo(() => {
    const resolution = 2048;

    // === SURFACE COLOR MAP ===
    const surfaceCanvas = document.createElement('canvas');
    surfaceCanvas.width = resolution;
    surfaceCanvas.height = resolution / 2;
    const surfaceCtx = surfaceCanvas.getContext('2d')!;

    // Base scorched grey-brown surface
    const baseGradient = surfaceCtx.createLinearGradient(0, 0, resolution, resolution / 2);
    baseGradient.addColorStop(0, '#4a4035');
    baseGradient.addColorStop(0.3, '#5c5045');
    baseGradient.addColorStop(0.5, '#3d3530');
    baseGradient.addColorStop(0.7, '#4f4540');
    baseGradient.addColorStop(1, '#3a3025');
    surfaceCtx.fillStyle = baseGradient;
    surfaceCtx.fillRect(0, 0, resolution, resolution / 2);

    // Add surface variation (scorched, volcanic regions)
    for (let i = 0; i < 5000; i++) {
      const x = Math.random() * resolution;
      const y = Math.random() * (resolution / 2);
      const noiseVal = fbm(x / 200, y / 200, 3);

      // Vary between dark scorched and lighter dusty regions
      const brightness = 40 + noiseVal * 60;
      const r = brightness + Math.random() * 20;
      const g = brightness - 5 + Math.random() * 15;
      const b = brightness - 15 + Math.random() * 10;

      surfaceCtx.fillStyle = `rgb(${r}, ${g}, ${b})`;
      surfaceCtx.beginPath();
      surfaceCtx.ellipse(x, y, 2 + Math.random() * 8, 2 + Math.random() * 6, Math.random() * Math.PI, 0, Math.PI * 2);
      surfaceCtx.fill();
    }

    // === CRATER GENERATION ===
    // Large impact basins (like Caloris)
    const largeCraters = [
      { x: 400, y: 300, r: 180, depth: 0.8 },
      { x: 1200, y: 400, r: 120, depth: 0.7 },
      { x: 1800, y: 250, r: 100, depth: 0.6 },
      { x: 600, y: 600, r: 90, depth: 0.65 },
    ];

    largeCraters.forEach(crater => {
      // Crater floor (darker)
      const floorGradient = surfaceCtx.createRadialGradient(
        crater.x, crater.y, 0,
        crater.x, crater.y, crater.r
      );
      floorGradient.addColorStop(0, '#2a2520');
      floorGradient.addColorStop(0.6, '#353025');
      floorGradient.addColorStop(0.85, '#4a4035');
      floorGradient.addColorStop(1, '#5a5045');

      surfaceCtx.fillStyle = floorGradient;
      surfaceCtx.beginPath();
      surfaceCtx.arc(crater.x, crater.y, crater.r, 0, Math.PI * 2);
      surfaceCtx.fill();

      // Crater rim (lighter, raised)
      surfaceCtx.strokeStyle = '#6a6055';
      surfaceCtx.lineWidth = 8;
      surfaceCtx.beginPath();
      surfaceCtx.arc(crater.x, crater.y, crater.r, 0, Math.PI * 2);
      surfaceCtx.stroke();

      // Central peak for large craters
      if (crater.r > 80) {
        surfaceCtx.fillStyle = '#5a5045';
        surfaceCtx.beginPath();
        surfaceCtx.arc(crater.x, crater.y, crater.r * 0.15, 0, Math.PI * 2);
        surfaceCtx.fill();
      }
    });

    // Medium craters
    for (let i = 0; i < 100; i++) {
      const x = Math.random() * resolution;
      const y = Math.random() * (resolution / 2);
      const r = 15 + Math.random() * 40;

      const craterGradient = surfaceCtx.createRadialGradient(x, y, 0, x, y, r);
      craterGradient.addColorStop(0, '#2d2825');
      craterGradient.addColorStop(0.7, '#3a3530');
      craterGradient.addColorStop(1, '#4a4540');

      surfaceCtx.fillStyle = craterGradient;
      surfaceCtx.beginPath();
      surfaceCtx.arc(x, y, r, 0, Math.PI * 2);
      surfaceCtx.fill();

      // Rim
      surfaceCtx.strokeStyle = '#5a5550';
      surfaceCtx.lineWidth = 2;
      surfaceCtx.stroke();
    }

    // Small craters (dense coverage)
    for (let i = 0; i < 800; i++) {
      const x = Math.random() * resolution;
      const y = Math.random() * (resolution / 2);
      const r = 2 + Math.random() * 10;

      surfaceCtx.fillStyle = `rgba(35, 30, 28, ${0.3 + Math.random() * 0.5})`;
      surfaceCtx.beginPath();
      surfaceCtx.arc(x, y, r, 0, Math.PI * 2);
      surfaceCtx.fill();
    }

    // Ejecta rays from fresh impacts
    for (let i = 0; i < 15; i++) {
      const cx = Math.random() * resolution;
      const cy = Math.random() * (resolution / 2);
      const rayCount = 4 + Math.floor(Math.random() * 8);

      surfaceCtx.strokeStyle = 'rgba(90, 85, 80, 0.3)';
      surfaceCtx.lineWidth = 1;

      for (let j = 0; j < rayCount; j++) {
        const angle = (j / rayCount) * Math.PI * 2;
        const length = 30 + Math.random() * 60;
        surfaceCtx.beginPath();
        surfaceCtx.moveTo(cx, cy);
        surfaceCtx.lineTo(cx + Math.cos(angle) * length, cy + Math.sin(angle) * length);
        surfaceCtx.stroke();
      }
    }

    const surface = new THREE.CanvasTexture(surfaceCanvas);
    surface.wrapS = THREE.RepeatWrapping;
    surface.wrapT = THREE.ClampToEdgeWrapping;
    surface.anisotropy = 16;

    // === BUMP MAP (Height/Displacement) ===
    const bumpCanvas = document.createElement('canvas');
    bumpCanvas.width = resolution;
    bumpCanvas.height = resolution / 2;
    const bumpCtx = bumpCanvas.getContext('2d')!;

    // Base terrain noise
    bumpCtx.fillStyle = '#808080'; // Neutral grey
    bumpCtx.fillRect(0, 0, resolution, resolution / 2);

    // Generate height from noise
    const bumpData = bumpCtx.getImageData(0, 0, resolution, resolution / 2);
    for (let y = 0; y < resolution / 2; y++) {
      for (let x = 0; x < resolution; x++) {
        const idx = (y * resolution + x) * 4;

        // Multi-scale terrain noise
        let height = 128;
        height += fbm(x / 100, y / 100, 4) * 40;
        height += fbm(x / 30, y / 30, 3) * 20;
        height += noise2D(x / 10, y / 10) * 10;

        // Clamp
        height = Math.max(0, Math.min(255, height));

        bumpData.data[idx] = height;
        bumpData.data[idx + 1] = height;
        bumpData.data[idx + 2] = height;
        bumpData.data[idx + 3] = 255;
      }
    }
    bumpCtx.putImageData(bumpData, 0, 0);

    // Add crater depressions to bump map
    largeCraters.forEach(crater => {
      const depthGradient = bumpCtx.createRadialGradient(
        crater.x, crater.y, 0,
        crater.x, crater.y, crater.r
      );
      depthGradient.addColorStop(0, `rgb(${60 * crater.depth}, ${60 * crater.depth}, ${60 * crater.depth})`);
      depthGradient.addColorStop(0.8, 'rgb(100, 100, 100)');
      depthGradient.addColorStop(0.95, 'rgb(160, 160, 160)');
      depthGradient.addColorStop(1, 'rgb(128, 128, 128)');

      bumpCtx.fillStyle = depthGradient;
      bumpCtx.beginPath();
      bumpCtx.arc(crater.x, crater.y, crater.r, 0, Math.PI * 2);
      bumpCtx.fill();
    });

    const bump = new THREE.CanvasTexture(bumpCanvas);
    bump.wrapS = THREE.RepeatWrapping;
    bump.wrapT = THREE.ClampToEdgeWrapping;

    // === ROUGHNESS MAP ===
    const roughnessCanvas = document.createElement('canvas');
    roughnessCanvas.width = 1024;
    roughnessCanvas.height = 512;
    const roughnessCtx = roughnessCanvas.getContext('2d')!;

    // Mostly rough surface
    roughnessCtx.fillStyle = '#cccccc';
    roughnessCtx.fillRect(0, 0, 1024, 512);

    // Smoother areas (lava plains)
    for (let i = 0; i < 20; i++) {
      const x = Math.random() * 1024;
      const y = Math.random() * 512;
      roughnessCtx.fillStyle = `rgba(100, 100, 100, ${0.3 + Math.random() * 0.3})`;
      roughnessCtx.beginPath();
      roughnessCtx.ellipse(x, y, 30 + Math.random() * 80, 20 + Math.random() * 50, Math.random() * Math.PI, 0, Math.PI * 2);
      roughnessCtx.fill();
    }

    const roughness = new THREE.CanvasTexture(roughnessCanvas);
    roughness.wrapS = THREE.RepeatWrapping;

    return { surfaceTexture: surface, bumpTexture: bump, roughnessTexture: roughness };
  }, []);

  // Generate infrastructure positions on surface
  const infrastructureData = useMemo(() => {
    const items: Array<{
      type: 'mine' | 'mirror' | 'massDriver' | 'processor' | 'habitat' | 'tower';
      theta: number;
      phi: number;
      scale: number;
    }> = [];

    // Mining complexes (clustered)
    const mineCount = 8;
    for (let i = 0; i < mineCount; i++) {
      const baseTheta = (i / mineCount) * Math.PI * 2;
      const basePhi = Math.PI * 0.3 + Math.random() * Math.PI * 0.4;

      items.push({
        type: 'mine',
        theta: baseTheta + (Math.random() - 0.5) * 0.3,
        phi: basePhi,
        scale: 0.8 + Math.random() * 0.4
      });
    }

    // Mirror arrays (optical relay infrastructure)
    const mirrorCount = 12;
    for (let i = 0; i < mirrorCount; i++) {
      items.push({
        type: 'mirror',
        theta: (i / mirrorCount) * Math.PI * 2 + Math.random() * 0.2,
        phi: Math.PI * 0.4 + Math.random() * 0.2,
        scale: 0.6 + Math.random() * 0.3
      });
    }

    // Mass driver (electromagnetic launcher)
    items.push({
      type: 'massDriver',
      theta: Math.PI * 0.25,
      phi: Math.PI * 0.5,
      scale: 1.2
    });

    // Processing facilities
    const processorCount = 5;
    for (let i = 0; i < processorCount; i++) {
      items.push({
        type: 'processor',
        theta: Math.PI * 0.5 + (i / processorCount) * Math.PI,
        phi: Math.PI * 0.45 + Math.random() * 0.1,
        scale: 0.7 + Math.random() * 0.3
      });
    }

    // Habitat domes
    const habitatCount = 3;
    for (let i = 0; i < habitatCount; i++) {
      items.push({
        type: 'habitat',
        theta: Math.PI * 1.5 + (i / habitatCount) * Math.PI * 0.5,
        phi: Math.PI * 0.5,
        scale: 0.5 + Math.random() * 0.2
      });
    }

    // Communication/sensor towers
    const towerCount = 6;
    for (let i = 0; i < towerCount; i++) {
      items.push({
        type: 'tower',
        theta: (i / towerCount) * Math.PI * 2,
        phi: Math.PI * 0.3,
        scale: 0.4 + Math.random() * 0.2
      });
    }

    return items;
  }, []);

  useFrame((state) => {
    // Mercury has very slow rotation (59 Earth days per rotation)
    // Using extremely slow rate for realism
    if (mercuryRef.current) {
      mercuryRef.current.rotation.y += 0.00008;
    }
    if (infrastructureRef.current) {
      infrastructureRef.current.rotation.y += 0.00008;
    }
  });

  // Convert spherical to cartesian for surface positioning
  const sphericalToCartesian = (r: number, theta: number, phi: number): [number, number, number] => {
    return [
      r * Math.sin(phi) * Math.cos(theta),
      r * Math.cos(phi),
      r * Math.sin(phi) * Math.sin(theta)
    ];
  };

  return (
    <group position={position} onClick={(e) => { e.stopPropagation(); onClick?.(); }}>
      {/* Dedicated lighting for Mercury detail */}
      <pointLight position={[-2, 1, 1]} color={0xffffff} intensity={2} distance={10} />
      <pointLight position={[1, -0.5, -1]} color={0xffaa66} intensity={0.8} distance={8} />

      {/* Main Mercury body - HIGH DETAIL */}
      <mesh ref={mercuryRef}>
        <sphereGeometry args={[radius, 128, 64]} />
        <meshStandardMaterial
          map={surfaceTexture}
          bumpMap={bumpTexture}
          bumpScale={0.03}
          roughnessMap={roughnessTexture}
          roughness={0.9}
          metalness={0.1}
        />
      </mesh>

      {/* Infrastructure group - rotates with planet */}
      <group ref={infrastructureRef}>
        {infrastructureData.map((item, index) => {
          const pos = sphericalToCartesian(radius * 1.01, item.theta, item.phi);
          const normal = new THREE.Vector3(...pos).normalize();

          return (
            <group key={index} position={pos}>
              {/* Mining complex */}
              {item.type === 'mine' && (
                <MiningComplex scale={item.scale * 0.08} normal={normal} />
              )}

              {/* Mirror array */}
              {item.type === 'mirror' && (
                <MirrorArray scale={item.scale * 0.06} normal={normal} />
              )}

              {/* Mass driver */}
              {item.type === 'massDriver' && (
                <MassDriver scale={item.scale * 0.1} normal={normal} />
              )}

              {/* Processing facility */}
              {item.type === 'processor' && (
                <ProcessingFacility scale={item.scale * 0.07} normal={normal} />
              )}

              {/* Habitat dome */}
              {item.type === 'habitat' && (
                <HabitatDome scale={item.scale * 0.08} normal={normal} />
              )}

              {/* Sensor tower */}
              {item.type === 'tower' && (
                <SensorTower scale={item.scale * 0.05} normal={normal} />
              )}
            </group>
          );
        })}
      </group>

      {/* Orbital relay station (the main optical hub) */}
      {/* Positioned on Sun-facing side for clear LOS from EAHP */}
      <OrbitalRelayHub position={relayHubPosition} />

      {/* Spaceport - DARK SIDE landing facility for mining transports */}
      {/* Ships land here to unload cargo, refuel, and return to EAHP */}
      <Spaceport position={spaceportPosition} sunPosition={sunPosition} />

      {/* Polar Starship Base - North pole landing facility for Earth Starships */}
      {/* Separate from mining operations, used by interplanetary vessels */}
      <PolarStarshipBase position={polarBasePosition} />

      {/* Label - hidden during clip mode */}
      {view.showLabels && !isClipMode && (
        <sprite position={[0, radius + 0.8, 0]} scale={[3, 0.75, 1]}>
          <spriteMaterial
            map={createMercuryLabel()}
            transparent
          />
        </sprite>
      )}
    </group>
  );
}

// === INFRASTRUCTURE COMPONENTS - HIGH DETAIL ===

function MiningComplex({ scale, normal }: { scale: number; normal: THREE.Vector3 }) {
  const groupRef = useRef<THREE.Group>(null);
  const drillRef = useRef<THREE.Mesh>(null);
  const conveyorRef = useRef<THREE.Group>(null);

  // Orient to surface normal
  useMemo(() => {
    if (groupRef.current) {
      groupRef.current.lookAt(normal.clone().multiplyScalar(2));
    }
  }, [normal]);

  // Animate drill rotation and conveyor
  useFrame((state) => {
    const time = state.clock.elapsedTime;
    if (drillRef.current) {
      drillRef.current.rotation.y = time * 3;
    }
  });

  return (
    <group ref={groupRef} scale={scale}>
      {/* Excavation pit (dark depression in surface) */}
      <mesh position={[0, -0.1, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <ringGeometry args={[0, 0.6, 16]} />
        <meshStandardMaterial color={0x222211} roughness={1} />
      </mesh>

      {/* Main excavator housing */}
      <mesh position={[0, 0.25, 0]}>
        <boxGeometry args={[1, 0.5, 0.8]} />
        <meshStandardMaterial color={0x665544} metalness={0.7} roughness={0.4} />
      </mesh>

      {/* Excavator cab */}
      <mesh position={[0.3, 0.55, 0]}>
        <boxGeometry args={[0.4, 0.3, 0.5]} />
        <meshStandardMaterial color={0x556655} metalness={0.6} roughness={0.3} />
      </mesh>

      {/* Cab windows */}
      <mesh position={[0.52, 0.55, 0]}>
        <boxGeometry args={[0.02, 0.15, 0.3]} />
        <meshBasicMaterial color={0x88ffff} transparent opacity={0.7} toneMapped={false} />
      </mesh>

      {/* Rotating drill tower */}
      <group position={[0, 0.5, 0]}>
        <mesh>
          <cylinderGeometry args={[0.15, 0.2, 0.4, 8]} />
          <meshStandardMaterial color={0x887766} metalness={0.8} roughness={0.3} />
        </mesh>
        <mesh ref={drillRef} position={[0, 0.5, 0]}>
          <coneGeometry args={[0.12, 0.8, 8]} />
          <meshStandardMaterial color={0x999988} metalness={0.9} roughness={0.2} />
        </mesh>
        {/* Drill bit segments */}
        {[0.2, 0.4, 0.6].map((y, i) => (
          <mesh key={i} position={[0, y + 0.2, 0]}>
            <torusGeometry args={[0.1 - i * 0.02, 0.015, 6, 12]} />
            <meshStandardMaterial color={0xaaaaaa} metalness={0.9} roughness={0.1} />
          </mesh>
        ))}
      </group>

      {/* Conveyor belt system */}
      <group ref={conveyorRef} position={[0.6, 0.15, 0]} rotation={[0, 0, Math.PI / 8]}>
        {/* Belt frame */}
        <mesh>
          <boxGeometry args={[1.2, 0.08, 0.2]} />
          <meshStandardMaterial color={0x444433} metalness={0.6} roughness={0.5} />
        </mesh>
        {/* Belt surface */}
        <mesh position={[0, 0.05, 0]}>
          <boxGeometry args={[1.15, 0.02, 0.15]} />
          <meshStandardMaterial color={0x222222} roughness={0.9} />
        </mesh>
        {/* Support legs */}
        {[-0.4, 0, 0.4].map((x, i) => (
          <mesh key={i} position={[x, -0.15, 0]}>
            <boxGeometry args={[0.04, 0.25, 0.04]} />
            <meshStandardMaterial color={0x555544} metalness={0.5} roughness={0.6} />
          </mesh>
        ))}
        {/* Ore chunks on belt */}
        {[-0.3, 0, 0.25].map((x, i) => (
          <mesh key={i} position={[x, 0.08, 0]}>
            <dodecahedronGeometry args={[0.04, 0]} />
            <meshStandardMaterial color={0x886644} roughness={0.8} />
          </mesh>
        ))}
      </group>

      {/* Storage silos */}
      {[[-0.5, 0.3], [-0.5, -0.3]].map(([x, z], i) => (
        <group key={i} position={[x, 0.3, z]}>
          <mesh>
            <cylinderGeometry args={[0.15, 0.18, 0.5, 8]} />
            <meshStandardMaterial color={0x667766} metalness={0.5} roughness={0.6} />
          </mesh>
          <mesh position={[0, 0.3, 0]}>
            <coneGeometry args={[0.15, 0.15, 8]} />
            <meshStandardMaterial color={0x556655} metalness={0.5} roughness={0.5} />
          </mesh>
          {/* Fill level indicator */}
          <mesh position={[0.16, 0, 0]}>
            <boxGeometry args={[0.02, 0.3, 0.02]} />
            <meshBasicMaterial color={0x00ff44} toneMapped={false} />
          </mesh>
        </group>
      ))}

      {/* Work lights */}
      {[[0.4, 0.8, 0.3], [-0.3, 0.7, -0.2]].map(([x, y, z], i) => (
        <group key={i} position={[x, y, z]}>
          <mesh>
            <sphereGeometry args={[0.04, 8, 8]} />
            <meshBasicMaterial color={0xffff88} toneMapped={false} />
          </mesh>
          <pointLight color={0xffffaa} intensity={0.5} distance={1.5} />
        </group>
      ))}

      {/* Warning beacon */}
      <mesh position={[0, 1.3, 0]}>
        <octahedronGeometry args={[0.06, 0]} />
        <meshBasicMaterial color={0xff4400} toneMapped={false} />
      </mesh>
      <pointLight position={[0, 1.3, 0]} color={0xff4400} intensity={0.5} distance={1.5} />
    </group>
  );
}

function MirrorArray({ scale, normal }: { scale: number; normal: THREE.Vector3 }) {
  const groupRef = useRef<THREE.Group>(null);
  const dishRef = useRef<THREE.Group>(null);

  useMemo(() => {
    if (groupRef.current) {
      groupRef.current.lookAt(normal.clone().multiplyScalar(2));
    }
  }, [normal]);

  // Slow tracking movement
  useFrame((state) => {
    if (dishRef.current) {
      dishRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.1) * 0.2;
    }
  });

  return (
    <group ref={groupRef} scale={scale}>
      {/* Base platform with rotation mechanism */}
      <mesh position={[0, 0.03, 0]}>
        <cylinderGeometry args={[0.35, 0.4, 0.06, 12]} />
        <meshStandardMaterial color={0x555566} metalness={0.7} roughness={0.4} />
      </mesh>

      {/* Rotation ring */}
      <mesh position={[0, 0.08, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.28, 0.02, 8, 24]} />
        <meshStandardMaterial color={0x666677} metalness={0.8} roughness={0.3} />
      </mesh>

      {/* Articulated mount */}
      <group ref={dishRef} position={[0, 0.1, 0]}>
        {/* Vertical support */}
        <mesh position={[0, 0.2, 0]}>
          <cylinderGeometry args={[0.04, 0.06, 0.35, 8]} />
          <meshStandardMaterial color={0x667788} metalness={0.7} roughness={0.4} />
        </mesh>

        {/* Pivot joint */}
        <mesh position={[0, 0.4, 0]}>
          <sphereGeometry args={[0.06, 12, 12]} />
          <meshStandardMaterial color={0x778899} metalness={0.8} roughness={0.3} />
        </mesh>

        {/* Mirror dish - highly reflective parabolic */}
        <group position={[0, 0.5, 0]} rotation={[Math.PI / 4, 0, 0]}>
          {/* Primary mirror */}
          <mesh>
            <circleGeometry args={[0.6, 24]} />
            <meshStandardMaterial
              color={0xeeeeff}
              metalness={1}
              roughness={0.02}
              envMapIntensity={3}
              side={THREE.DoubleSide}
            />
          </mesh>

          {/* Mirror rim */}
          <mesh>
            <ringGeometry args={[0.58, 0.65, 24]} />
            <meshStandardMaterial color={0x556677} metalness={0.8} roughness={0.3} side={THREE.DoubleSide} />
          </mesh>

          {/* Focus receiver at center */}
          <mesh position={[0, 0, 0.4]}>
            <cylinderGeometry args={[0.05, 0.03, 0.15, 8]} />
            <meshStandardMaterial color={0x333344} metalness={0.6} roughness={0.4} />
          </mesh>

          {/* Receiver glow - collecting light */}
          <mesh position={[0, 0, 0.35]}>
            <sphereGeometry args={[0.04, 8, 8]} />
            <meshBasicMaterial color={0xffcc00} toneMapped={false} />
          </mesh>

          {/* Support struts to receiver */}
          {[0, Math.PI * 2/3, Math.PI * 4/3].map((angle, i) => (
            <mesh
              key={i}
              position={[Math.cos(angle) * 0.25, Math.sin(angle) * 0.25, 0.2]}
              rotation={[0.6, 0, angle]}
            >
              <cylinderGeometry args={[0.01, 0.01, 0.35, 4]} />
              <meshStandardMaterial color={0x556666} metalness={0.6} roughness={0.5} />
            </mesh>
          ))}
        </group>
      </group>

      {/* Status indicator */}
      <mesh position={[0.3, 0.1, 0]}>
        <sphereGeometry args={[0.025, 6, 6]} />
        <meshBasicMaterial color={0x00ff44} toneMapped={false} />
      </mesh>
    </group>
  );
}

function MassDriver({ scale, normal }: { scale: number; normal: THREE.Vector3 }) {
  const groupRef = useRef<THREE.Group>(null);
  const chargeRef = useRef<number>(0);

  useMemo(() => {
    if (groupRef.current) {
      groupRef.current.lookAt(normal.clone().multiplyScalar(2));
    }
  }, [normal]);

  return (
    <group ref={groupRef} scale={scale}>
      {/* Foundation platform */}
      <mesh position={[0, 0.03, 0]}>
        <boxGeometry args={[4, 0.06, 0.8]} />
        <meshStandardMaterial color={0x444455} metalness={0.6} roughness={0.5} />
      </mesh>

      {/* Main rail structure - elevated track */}
      <group position={[0, 0.2, 0]} rotation={[0, 0, Math.PI / 12]}>
        {/* Twin rails */}
        {[-0.12, 0.12].map((z, i) => (
          <mesh key={i} position={[0, 0.08, z]}>
            <boxGeometry args={[3.5, 0.08, 0.06]} />
            <meshStandardMaterial color={0x778899} metalness={0.9} roughness={0.2} />
          </mesh>
        ))}

        {/* Rail ties/supports */}
        {Array.from({ length: 20 }).map((_, i) => (
          <mesh key={i} position={[-1.6 + i * 0.18, 0.04, 0]}>
            <boxGeometry args={[0.04, 0.04, 0.35]} />
            <meshStandardMaterial color={0x556666} metalness={0.5} roughness={0.6} />
          </mesh>
        ))}

        {/* Electromagnetic acceleration coils */}
        {Array.from({ length: 8 }).map((_, i) => (
          <group key={i} position={[-1.4 + i * 0.4, 0.12, 0]}>
            <mesh rotation={[Math.PI / 2, 0, 0]}>
              <torusGeometry args={[0.15, 0.025, 8, 16]} />
              <meshStandardMaterial
                color={0x3377aa}
                metalness={0.9}
                roughness={0.2}
                emissive={0x1144aa}
                emissiveIntensity={0.8}
              />
            </mesh>
            {/* Coil housing */}
            <mesh>
              <boxGeometry args={[0.08, 0.35, 0.35]} />
              <meshStandardMaterial color={0x445566} metalness={0.7} roughness={0.4} />
            </mesh>
          </group>
        ))}

        {/* Launch bucket/payload container */}
        <mesh position={[-1.2, 0.15, 0]}>
          <boxGeometry args={[0.25, 0.12, 0.18]} />
          <meshStandardMaterial color={0x887766} metalness={0.5} roughness={0.5} />
        </mesh>
      </group>

      {/* Power station - larger and more detailed */}
      <group position={[-1.8, 0.1, 0.35]}>
        {/* Main power building */}
        <mesh position={[0, 0.25, 0]}>
          <boxGeometry args={[0.6, 0.5, 0.5]} />
          <meshStandardMaterial color={0x556677} metalness={0.6} roughness={0.4} />
        </mesh>

        {/* Capacitor banks */}
        {[-0.15, 0.15].map((x, i) => (
          <mesh key={i} position={[x, 0.55, 0]}>
            <cylinderGeometry args={[0.08, 0.08, 0.25, 8]} />
            <meshStandardMaterial
              color={0x4466aa}
              metalness={0.8}
              roughness={0.3}
              emissive={0x223366}
              emissiveIntensity={0.5}
            />
          </mesh>
        ))}

        {/* High voltage warning */}
        <mesh position={[0.32, 0.35, 0]}>
          <boxGeometry args={[0.02, 0.2, 0.15]} />
          <meshBasicMaterial color={0xffff00} />
        </mesh>

        {/* Power cables to track */}
        <mesh position={[0.4, 0.2, -0.2]} rotation={[0, 0, Math.PI / 4]}>
          <cylinderGeometry args={[0.02, 0.02, 0.5, 6]} />
          <meshStandardMaterial color={0x333333} roughness={0.8} />
        </mesh>
      </group>

      {/* Support pylons - A-frame design */}
      {[-1, 0, 1].map((x, i) => (
        <group key={i} position={[x, 0, 0]}>
          {[-0.2, 0.2].map((z, j) => (
            <mesh key={j} position={[0, 0.12, z]} rotation={[z > 0 ? 0.15 : -0.15, 0, 0]}>
              <boxGeometry args={[0.06, 0.25, 0.06]} />
              <meshStandardMaterial color={0x556655} metalness={0.6} roughness={0.5} />
            </mesh>
          ))}
          {/* Cross brace */}
          <mesh position={[0, 0.18, 0]}>
            <boxGeometry args={[0.04, 0.04, 0.35]} />
            <meshStandardMaterial color={0x556655} metalness={0.6} roughness={0.5} />
          </mesh>
        </group>
      ))}

      {/* Launch exit - muzzle end */}
      <group position={[1.9, 0.35, 0]}>
        <mesh rotation={[0, 0, Math.PI / 12]}>
          <cylinderGeometry args={[0.12, 0.15, 0.3, 8]} />
          <meshStandardMaterial color={0x667788} metalness={0.8} roughness={0.3} />
        </mesh>
        {/* Exit glow */}
        <mesh position={[0.2, 0.05, 0]}>
          <sphereGeometry args={[0.08, 8, 8]} />
          <meshBasicMaterial color={0x00aaff} transparent opacity={0.6} toneMapped={false} />
        </mesh>
        <pointLight position={[0.2, 0.05, 0]} color={0x00aaff} intensity={1} distance={2} />
      </group>

      {/* Control booth */}
      <group position={[-1.5, 0.1, -0.4]}>
        <mesh position={[0, 0.15, 0]}>
          <boxGeometry args={[0.35, 0.3, 0.25]} />
          <meshStandardMaterial color={0x667766} metalness={0.5} roughness={0.5} />
        </mesh>
        {/* Window */}
        <mesh position={[0.18, 0.18, 0]}>
          <boxGeometry args={[0.02, 0.12, 0.15]} />
          <meshBasicMaterial color={0x88ffff} transparent opacity={0.7} toneMapped={false} />
        </mesh>
      </group>

      {/* Status lights along track */}
      {[-1, 0, 1].map((x, i) => (
        <mesh key={i} position={[x, 0.4, 0.25]}>
          <sphereGeometry args={[0.025, 6, 6]} />
          <meshBasicMaterial color={i === 2 ? 0x00ff00 : 0xffaa00} toneMapped={false} />
        </mesh>
      ))}
    </group>
  );
}

function ProcessingFacility({ scale, normal }: { scale: number; normal: THREE.Vector3 }) {
  const groupRef = useRef<THREE.Group>(null);

  useMemo(() => {
    if (groupRef.current) {
      groupRef.current.lookAt(normal.clone().multiplyScalar(2));
    }
  }, [normal]);

  return (
    <group ref={groupRef} scale={scale}>
      {/* Foundation slab */}
      <mesh position={[0, 0.03, 0]}>
        <boxGeometry args={[1.2, 0.06, 0.9]} />
        <meshStandardMaterial color={0x444444} metalness={0.5} roughness={0.7} />
      </mesh>

      {/* Main processing building */}
      <mesh position={[0, 0.3, 0]}>
        <boxGeometry args={[0.8, 0.5, 0.6]} />
        <meshStandardMaterial color={0x5a5a50} metalness={0.5} roughness={0.5} />
      </mesh>

      {/* Roof with ventilation */}
      <mesh position={[0, 0.58, 0]}>
        <boxGeometry args={[0.85, 0.06, 0.65]} />
        <meshStandardMaterial color={0x666655} metalness={0.6} roughness={0.4} />
      </mesh>

      {/* Industrial doors */}
      <mesh position={[0.41, 0.22, 0]}>
        <boxGeometry args={[0.02, 0.35, 0.4]} />
        <meshStandardMaterial color={0x445544} metalness={0.4} roughness={0.6} />
      </mesh>

      {/* Refinery distillation towers */}
      {[-0.25, 0, 0.25].map((x, i) => (
        <group key={i} position={[x, 0.6, -0.15]}>
          {/* Main column */}
          <mesh position={[0, 0.35, 0]}>
            <cylinderGeometry args={[0.08 - i * 0.01, 0.1 - i * 0.01, 0.7 + i * 0.15, 12]} />
            <meshStandardMaterial color={0x888877} metalness={0.7} roughness={0.3} />
          </mesh>
          {/* Column bands */}
          {[0.1, 0.3, 0.5].map((y, j) => (
            <mesh key={j} position={[0, y, 0]}>
              <torusGeometry args={[0.09 - i * 0.01, 0.015, 8, 16]} />
              <meshStandardMaterial color={0x666655} metalness={0.6} roughness={0.4} />
            </mesh>
          ))}
          {/* Top cap */}
          <mesh position={[0, 0.75 + i * 0.08, 0]}>
            <sphereGeometry args={[0.07 - i * 0.01, 8, 8, 0, Math.PI * 2, 0, Math.PI / 2]} />
            <meshStandardMaterial color={0x999988} metalness={0.6} roughness={0.3} />
          </mesh>
        </group>
      ))}

      {/* Pipe network */}
      {[
        { from: [0.15, 0.5, 0.2], to: [0.4, 0.3, 0.3], color: 0x666666 },
        { from: [-0.15, 0.6, 0.2], to: [-0.15, 0.6, 0.4], color: 0x886644 },
        { from: [0, 0.4, 0.31], to: [0.35, 0.4, 0.31], color: 0x448866 },
      ].map((pipe, i) => {
        const start = new THREE.Vector3(...pipe.from);
        const end = new THREE.Vector3(...pipe.to);
        const mid = start.clone().add(end).multiplyScalar(0.5);
        const length = start.distanceTo(end);
        const direction = end.clone().sub(start).normalize();
        const quaternion = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction);

        return (
          <mesh key={i} position={mid} quaternion={quaternion}>
            <cylinderGeometry args={[0.025, 0.025, length, 6]} />
            <meshStandardMaterial color={pipe.color} metalness={0.6} roughness={0.4} />
          </mesh>
        );
      })}

      {/* Storage tanks */}
      {[[0.45, 0.2, 0.25], [0.45, 0.2, -0.2]].map(([x, y, z], i) => (
        <group key={i} position={[x, y, z]}>
          <mesh rotation={[0, 0, Math.PI / 2]}>
            <cylinderGeometry args={[0.12, 0.12, 0.25, 12]} />
            <meshStandardMaterial color={i === 0 ? 0x557755 : 0x775555} metalness={0.5} roughness={0.5} />
          </mesh>
          {/* Tank supports */}
          {[-0.08, 0.08].map((offset, j) => (
            <mesh key={j} position={[offset, -0.1, 0]}>
              <boxGeometry args={[0.03, 0.08, 0.03]} />
              <meshStandardMaterial color={0x444444} metalness={0.5} roughness={0.6} />
            </mesh>
          ))}
        </group>
      ))}

      {/* Exhaust stacks with glow */}
      {[[-0.3, 1.1, -0.15], [0.1, 1.2, -0.15]].map(([x, y, z], i) => (
        <group key={i} position={[x, y, z]}>
          <mesh>
            <cylinderGeometry args={[0.04, 0.05, 0.15, 8]} />
            <meshStandardMaterial color={0x555555} metalness={0.6} roughness={0.4} />
          </mesh>
          {/* Heat shimmer/exhaust glow */}
          <mesh position={[0, 0.12, 0]}>
            <sphereGeometry args={[0.06, 8, 8]} />
            <meshBasicMaterial color={0xff6600} transparent opacity={0.5} toneMapped={false} />
          </mesh>
          <pointLight position={[0, 0.1, 0]} color={0xff4400} intensity={0.4} distance={0.8} />
        </group>
      ))}

      {/* Control panel / monitoring station */}
      <group position={[-0.35, 0.15, 0.35]}>
        <mesh>
          <boxGeometry args={[0.2, 0.25, 0.08]} />
          <meshStandardMaterial color={0x556666} metalness={0.5} roughness={0.5} />
        </mesh>
        {/* Screen */}
        <mesh position={[0, 0.02, 0.045]}>
          <boxGeometry args={[0.12, 0.1, 0.01]} />
          <meshBasicMaterial color={0x00aa88} toneMapped={false} />
        </mesh>
      </group>

      {/* Work lights */}
      <pointLight position={[0, 0.8, 0.4]} color={0xffffcc} intensity={0.4} distance={1.5} />
    </group>
  );
}

function HabitatDome({ scale, normal }: { scale: number; normal: THREE.Vector3 }) {
  const groupRef = useRef<THREE.Group>(null);

  useMemo(() => {
    if (groupRef.current) {
      groupRef.current.lookAt(normal.clone().multiplyScalar(2));
    }
  }, [normal]);

  return (
    <group ref={groupRef} scale={scale}>
      {/* Foundation platform */}
      <mesh position={[0, 0.02, 0]}>
        <cylinderGeometry args={[0.55, 0.6, 0.04, 16]} />
        <meshStandardMaterial color={0x444455} metalness={0.6} roughness={0.5} />
      </mesh>

      {/* Base ring structure */}
      <mesh position={[0, 0.08, 0]}>
        <cylinderGeometry args={[0.48, 0.52, 0.12, 16]} />
        <meshStandardMaterial color={0x556666} metalness={0.6} roughness={0.4} />
      </mesh>

      {/* Dome support ring */}
      <mesh position={[0, 0.15, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.45, 0.03, 8, 24]} />
        <meshStandardMaterial color={0x667777} metalness={0.7} roughness={0.3} />
      </mesh>

      {/* Main transparent dome */}
      <mesh position={[0, 0.15, 0]}>
        <sphereGeometry args={[0.45, 24, 24, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial
          color={0x99bbdd}
          metalness={0.2}
          roughness={0.1}
          transparent
          opacity={0.6}
          envMapIntensity={2}
        />
      </mesh>

      {/* Dome frame ribs */}
      {[0, Math.PI/4, Math.PI/2, Math.PI*3/4, Math.PI, Math.PI*5/4, Math.PI*3/2, Math.PI*7/4].map((angle, i) => (
        <mesh
          key={i}
          position={[Math.cos(angle) * 0.43, 0.35, Math.sin(angle) * 0.43]}
          rotation={[0, -angle, Math.PI/4]}
        >
          <boxGeometry args={[0.02, 0.55, 0.02]} />
          <meshStandardMaterial color={0x556677} metalness={0.7} roughness={0.4} />
        </mesh>
      ))}

      {/* Top ventilation unit */}
      <mesh position={[0, 0.62, 0]}>
        <cylinderGeometry args={[0.08, 0.06, 0.08, 8]} />
        <meshStandardMaterial color={0x667788} metalness={0.6} roughness={0.4} />
      </mesh>

      {/* Airlock module */}
      <group position={[0.5, 0.12, 0]}>
        {/* Airlock chamber */}
        <mesh>
          <boxGeometry args={[0.2, 0.2, 0.18]} />
          <meshStandardMaterial color={0x556655} metalness={0.5} roughness={0.5} />
        </mesh>
        {/* Outer door */}
        <mesh position={[0.11, 0, 0]}>
          <boxGeometry args={[0.02, 0.15, 0.12]} />
          <meshStandardMaterial color={0x445544} metalness={0.6} roughness={0.4} />
        </mesh>
        {/* Door frame */}
        <mesh position={[0.105, 0, 0]}>
          <boxGeometry args={[0.01, 0.18, 0.15]} />
          <meshStandardMaterial color={0x667766} metalness={0.7} roughness={0.3} />
        </mesh>
        {/* Status light */}
        <mesh position={[0.12, 0.12, 0]}>
          <sphereGeometry args={[0.02, 6, 6]} />
          <meshBasicMaterial color={0x00ff00} toneMapped={false} />
        </mesh>
      </group>

      {/* Life support unit */}
      <group position={[-0.4, 0.1, 0.25]}>
        <mesh>
          <boxGeometry args={[0.15, 0.18, 0.12]} />
          <meshStandardMaterial color={0x557766} metalness={0.5} roughness={0.5} />
        </mesh>
        {/* Vents */}
        {[-0.04, 0.04].map((y, i) => (
          <mesh key={i} position={[0.08, y, 0]}>
            <boxGeometry args={[0.02, 0.03, 0.08]} />
            <meshStandardMaterial color={0x333333} roughness={0.9} />
          </mesh>
        ))}
      </group>

      {/* Solar panel array for habitat power */}
      <group position={[-0.35, 0.08, -0.35]} rotation={[0, Math.PI/4, 0]}>
        <mesh position={[0, 0.08, 0]}>
          <cylinderGeometry args={[0.02, 0.03, 0.12, 6]} />
          <meshStandardMaterial color={0x666666} metalness={0.6} roughness={0.5} />
        </mesh>
        <mesh position={[0, 0.15, 0]} rotation={[0.3, 0, 0]}>
          <boxGeometry args={[0.3, 0.01, 0.15]} />
          <meshStandardMaterial
            color={0x2244aa}
            metalness={0.3}
            roughness={0.5}
            emissive={0x112244}
            emissiveIntensity={0.2}
          />
        </mesh>
      </group>

      {/* Interior lighting (warm glow) */}
      <pointLight position={[0, 0.3, 0]} color={0xffffee} intensity={0.5} distance={1.2} />

      {/* Exterior path lights */}
      {[[0.4, 0.05, 0.15], [0.3, 0.05, -0.2]].map(([x, y, z], i) => (
        <mesh key={i} position={[x, y, z]}>
          <cylinderGeometry args={[0.015, 0.02, 0.06, 6]} />
          <meshBasicMaterial color={0xffffaa} toneMapped={false} />
        </mesh>
      ))}
    </group>
  );
}

function SensorTower({ scale, normal }: { scale: number; normal: THREE.Vector3 }) {
  const groupRef = useRef<THREE.Group>(null);
  const radarDishRef = useRef<THREE.Group>(null);

  useMemo(() => {
    if (groupRef.current) {
      groupRef.current.lookAt(normal.clone().multiplyScalar(2));
    }
  }, [normal]);

  // Rotate radar dish
  useFrame((state) => {
    if (radarDishRef.current) {
      radarDishRef.current.rotation.y = state.clock.elapsedTime * 0.5;
    }
  });

  return (
    <group ref={groupRef} scale={scale}>
      {/* Foundation base */}
      <mesh position={[0, 0.03, 0]}>
        <cylinderGeometry args={[0.15, 0.18, 0.06, 8]} />
        <meshStandardMaterial color={0x444455} metalness={0.6} roughness={0.5} />
      </mesh>

      {/* Tower base housing */}
      <mesh position={[0, 0.12, 0]}>
        <boxGeometry args={[0.2, 0.15, 0.2]} />
        <meshStandardMaterial color={0x556666} metalness={0.5} roughness={0.5} />
      </mesh>

      {/* Main tower mast - lattice style */}
      <group position={[0, 0.2, 0]}>
        {/* Corner posts */}
        {[[0.04, 0.04], [0.04, -0.04], [-0.04, 0.04], [-0.04, -0.04]].map(([x, z], i) => (
          <mesh key={i} position={[x, 0.45, z]}>
            <cylinderGeometry args={[0.015, 0.02, 0.9, 6]} />
            <meshStandardMaterial color={0x888899} metalness={0.8} roughness={0.3} />
          </mesh>
        ))}

        {/* Cross braces */}
        {[0.2, 0.5, 0.8].map((y, i) => (
          <group key={i} position={[0, y, 0]}>
            {/* Horizontal ring */}
            <mesh rotation={[Math.PI / 2, 0, 0]}>
              <torusGeometry args={[0.055, 0.008, 4, 8]} />
              <meshStandardMaterial color={0x777788} metalness={0.7} roughness={0.4} />
            </mesh>
          </group>
        ))}
      </group>

      {/* Top platform */}
      <mesh position={[0, 1.1, 0]}>
        <cylinderGeometry args={[0.12, 0.1, 0.04, 8]} />
        <meshStandardMaterial color={0x667788} metalness={0.6} roughness={0.4} />
      </mesh>

      {/* Rotating radar dish */}
      <group ref={radarDishRef} position={[0, 1.2, 0]}>
        {/* Dish mount */}
        <mesh>
          <cylinderGeometry args={[0.03, 0.04, 0.08, 6]} />
          <meshStandardMaterial color={0x778899} metalness={0.7} roughness={0.3} />
        </mesh>

        {/* Radar dish */}
        <group position={[0, 0.1, 0]} rotation={[Math.PI / 4, 0, 0]}>
          <mesh>
            <coneGeometry args={[0.2, 0.12, 16, 1, true]} />
            <meshStandardMaterial
              color={0xbbbbcc}
              metalness={0.9}
              roughness={0.1}
              side={THREE.DoubleSide}
            />
          </mesh>
          {/* Feed horn */}
          <mesh position={[0, 0, 0.08]}>
            <cylinderGeometry args={[0.02, 0.03, 0.06, 6]} />
            <meshStandardMaterial color={0x555566} metalness={0.7} roughness={0.4} />
          </mesh>
        </group>
      </group>

      {/* Secondary antenna array */}
      <group position={[0.08, 1.3, 0]}>
        {/* Vertical antenna */}
        <mesh>
          <cylinderGeometry args={[0.008, 0.008, 0.25, 4]} />
          <meshStandardMaterial color={0x999999} metalness={0.8} roughness={0.2} />
        </mesh>
        {/* Antenna tip */}
        <mesh position={[0, 0.14, 0]}>
          <sphereGeometry args={[0.015, 6, 6]} />
          <meshStandardMaterial color={0xaaaaaa} metalness={0.9} roughness={0.1} />
        </mesh>
      </group>

      {/* Communication dish (fixed) */}
      <group position={[-0.1, 1.15, 0]} rotation={[0.3, 0.5, 0]}>
        <mesh>
          <circleGeometry args={[0.08, 12]} />
          <meshStandardMaterial
            color={0xccccdd}
            metalness={0.95}
            roughness={0.05}
            side={THREE.DoubleSide}
          />
        </mesh>
      </group>

      {/* Warning beacon light (red, blinking simulated by glow) */}
      <mesh position={[0, 1.45, 0]}>
        <octahedronGeometry args={[0.025, 0]} />
        <meshBasicMaterial color={0xff0000} toneMapped={false} />
      </mesh>
      <pointLight position={[0, 1.45, 0]} color={0xff0000} intensity={0.4} distance={1} />

      {/* Status LEDs on base */}
      {[[0.1, 0.15, 0.11], [-0.1, 0.15, 0.11]].map(([x, y, z], i) => (
        <mesh key={i} position={[x, y, z]}>
          <sphereGeometry args={[0.012, 6, 6]} />
          <meshBasicMaterial color={i === 0 ? 0x00ff00 : 0x00aaff} toneMapped={false} />
        </mesh>
      ))}
    </group>
  );
}

function OrbitalRelayHub({ position }: { position: [number, number, number] }) {
  const hubRef = useRef<THREE.Group>(null);
  const primaryMirrorRef = useRef<THREE.Group>(null);
  const secondaryMirrorRef = useRef<THREE.Group>(null);
  const solarArrayRef = useRef<THREE.Group>(null);
  const beamGuideRef = useRef<THREE.Mesh>(null);

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    if (hubRef.current) {
      // Slow station rotation
      hubRef.current.rotation.y = t * 0.05;
    }
    if (primaryMirrorRef.current) {
      // Subtle mirror tracking motion
      primaryMirrorRef.current.rotation.x = Math.sin(t * 0.2) * 0.02;
      primaryMirrorRef.current.rotation.z = Math.cos(t * 0.15) * 0.015;
    }
    if (secondaryMirrorRef.current) {
      // Counter-rotation for beam alignment
      secondaryMirrorRef.current.rotation.y = -t * 0.08;
    }
    if (solarArrayRef.current) {
      // Sun tracking
      solarArrayRef.current.rotation.y = Math.sin(t * 0.1) * 0.1;
    }
    if (beamGuideRef.current) {
      // Pulsing beam guide
      const mat = beamGuideRef.current.material as THREE.MeshBasicMaterial;
      mat.opacity = 0.4 + Math.sin(t * 3) * 0.2;
    }
  });

  // Shared materials
  const whiteMetal = { color: 0xe0e0e0, metalness: 0.85, roughness: 0.2 };
  const darkMetal = { color: 0x3a3a3a, metalness: 0.9, roughness: 0.25 };
  const goldMLI = { color: 0xd4a84b, metalness: 0.85, roughness: 0.25 };
  const copperHeat = { color: 0xb87333, metalness: 0.9, roughness: 0.2 };

  return (
    <group ref={hubRef} position={position}>
      {/* ========== CENTRAL HUB STRUCTURE ========== */}
      <group>
        {/* Main hub body - hexagonal prism */}
        <mesh>
          <cylinderGeometry args={[0.2, 0.18, 0.35, 6]} />
          <meshStandardMaterial {...whiteMetal} />
        </mesh>

        {/* Hub detail rings */}
        {[-0.12, 0, 0.12].map((y, i) => (
          <mesh key={`ring-${i}`} position={[0, y, 0]}>
            <torusGeometry args={[0.21, 0.012, 6, 6]} />
            <meshStandardMaterial {...darkMetal} />
          </mesh>
        ))}

        {/* Central core - illuminated control module */}
        <mesh position={[0, 0, 0]}>
          <dodecahedronGeometry args={[0.08, 0]} />
          <meshStandardMaterial
            color={0x00ddff}
            emissive={0x0088aa}
            emissiveIntensity={1}
            metalness={0.7}
            roughness={0.2}
            toneMapped={false}
          />
        </mesh>

        {/* Core glow */}
        <mesh position={[0, 0, 0]}>
          <sphereGeometry args={[0.12, 16, 16]} />
          <meshBasicMaterial
            color={0x00ccff}
            transparent
            opacity={0.25}
            blending={THREE.AdditiveBlending}
            toneMapped={false}
          />
        </mesh>

        {/* Hub window ports - viewing stations */}
        {[0, 1, 2, 3, 4, 5].map((i) => {
          const angle = (i / 6) * Math.PI * 2;
          return (
            <group key={`port-${i}`} position={[Math.cos(angle) * 0.185, 0, Math.sin(angle) * 0.185]}>
              <mesh>
                <boxGeometry args={[0.04, 0.08, 0.015]} />
                <meshBasicMaterial
                  color={0x88ddff}
                  transparent
                  opacity={0.8}
                  toneMapped={false}
                />
              </mesh>
              {/* Port frame */}
              <mesh position={[0, 0, 0.008]}>
                <boxGeometry args={[0.05, 0.09, 0.005]} />
                <meshStandardMaterial {...darkMetal} />
              </mesh>
            </group>
          );
        })}
      </group>

      {/* ========== PRIMARY FOCUSING MIRROR ========== */}
      <group ref={primaryMirrorRef} position={[0.55, 0, 0]}>
        {/* Large parabolic dish - faces toward incoming beam (EAHP) */}
        <mesh rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.5, 0.5, 0.02, 32]} />
          <meshStandardMaterial
            color={0xffffee}
            metalness={1}
            roughness={0}
            emissive={0xffdd88}
            emissiveIntensity={0.3}
          />
        </mesh>

        {/* Mirror reflective surface overlay */}
        <mesh rotation={[0, 0, Math.PI / 2]} position={[0.015, 0, 0]}>
          <cylinderGeometry args={[0.48, 0.48, 0.005, 32]} />
          <meshBasicMaterial
            color={0xffffcc}
            transparent
            opacity={0.6}
            blending={THREE.AdditiveBlending}
            toneMapped={false}
          />
        </mesh>

        {/* Concentric focusing rings */}
        {[0.15, 0.28, 0.4].map((r, i) => (
          <mesh key={`focus-ring-${i}`} rotation={[0, 0, Math.PI / 2]} position={[0.02, 0, 0]}>
            <torusGeometry args={[r, 0.006, 4, 32]} />
            <meshBasicMaterial
              color={0xffcc66}
              transparent
              opacity={0.5}
              toneMapped={false}
            />
          </mesh>
        ))}

        {/* Mirror support struts */}
        {[0, 1, 2, 3].map((i) => {
          const angle = (i / 4) * Math.PI * 2 + Math.PI / 4;
          return (
            <mesh
              key={`strut-${i}`}
              position={[0, Math.cos(angle) * 0.3, Math.sin(angle) * 0.3]}
              rotation={[angle, 0, 0]}
            >
              <boxGeometry args={[0.04, 0.4, 0.02]} />
              <meshStandardMaterial {...whiteMetal} />
            </mesh>
          );
        })}

        {/* Outer structural ring */}
        <mesh rotation={[0, 0, Math.PI / 2]}>
          <torusGeometry args={[0.52, 0.025, 8, 32]} />
          <meshStandardMaterial {...whiteMetal} />
        </mesh>

        {/* Mount arm to hub */}
        <mesh position={[-0.2, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.035, 0.04, 0.35, 8]} />
          <meshStandardMaterial {...darkMetal} />
        </mesh>
      </group>

      {/* ========== SECONDARY BEAM REDIRECTOR ========== */}
      <group ref={secondaryMirrorRef} position={[-0.45, 0.1, 0]}>
        {/* Smaller redirect mirror - sends beam to Earth */}
        <mesh rotation={[0.2, Math.PI / 4, 0]}>
          <cylinderGeometry args={[0.22, 0.22, 0.015, 24]} />
          <meshStandardMaterial
            color={0xeeffee}
            metalness={1}
            roughness={0}
            emissive={0x88ffaa}
            emissiveIntensity={0.4}
          />
        </mesh>

        {/* Active tracking rings */}
        {[0.08, 0.15].map((r, i) => (
          <mesh key={`sec-ring-${i}`} rotation={[0.2, Math.PI / 4, 0]} position={[0, 0.01, 0]}>
            <torusGeometry args={[r, 0.004, 4, 24]} />
            <meshBasicMaterial
              color={0x88ffaa}
              transparent
              opacity={0.6}
              toneMapped={false}
            />
          </mesh>
        ))}

        {/* Mirror frame */}
        <mesh rotation={[0.2, Math.PI / 4, 0]}>
          <torusGeometry args={[0.24, 0.015, 6, 24]} />
          <meshStandardMaterial {...whiteMetal} />
        </mesh>

        {/* Gimbal mount */}
        <group position={[0, -0.12, 0]}>
          <mesh>
            <sphereGeometry args={[0.05, 12, 12]} />
            <meshStandardMaterial {...darkMetal} />
          </mesh>
          {/* Actuator arms */}
          {[0, 1, 2].map((i) => {
            const angle = (i / 3) * Math.PI * 2;
            return (
              <mesh
                key={`act-${i}`}
                position={[Math.cos(angle) * 0.08, 0, Math.sin(angle) * 0.08]}
                rotation={[0, 0, angle]}
              >
                <cylinderGeometry args={[0.008, 0.008, 0.12, 6]} />
                <meshStandardMaterial color={0x666666} metalness={0.8} roughness={0.3} />
              </mesh>
            );
          })}
        </group>

        {/* Support arm to hub */}
        <mesh position={[0.18, -0.05, 0]} rotation={[0, 0, Math.PI / 3]}>
          <cylinderGeometry args={[0.02, 0.025, 0.3, 6]} />
          <meshStandardMaterial {...darkMetal} />
        </mesh>
      </group>

      {/* ========== BEAM GUIDE TUBE ========== */}
      <group position={[0.15, 0, 0]}>
        {/* Main optical tube - connects mirrors */}
        <mesh rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.06, 0.06, 0.6, 12, 1, true]} />
          <meshStandardMaterial
            {...darkMetal}
            side={THREE.DoubleSide}
            transparent
            opacity={0.85}
          />
        </mesh>

        {/* Internal beam glow */}
        <mesh ref={beamGuideRef} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.04, 0.04, 0.55, 12]} />
          <meshBasicMaterial
            color={0xffcc44}
            transparent
            opacity={0.4}
            blending={THREE.AdditiveBlending}
            toneMapped={false}
          />
        </mesh>

        {/* Tube support rings */}
        {[-0.2, 0, 0.2].map((x, i) => (
          <mesh key={`tube-ring-${i}`} position={[x, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
            <torusGeometry args={[0.065, 0.008, 4, 12]} />
            <meshStandardMaterial {...whiteMetal} />
          </mesh>
        ))}
      </group>

      {/* ========== SOLAR PANEL ARRAYS ========== */}
      <group ref={solarArrayRef}>
        {/* Twin solar arrays - provide power for beam amplification */}
        {[-1, 1].map((dir, i) => (
          <group key={`solar-${i}`} position={[0, dir * 0.55, 0]}>
            {/* Main panel structure */}
            <mesh>
              <boxGeometry args={[0.6, 0.02, 0.35]} />
              <meshStandardMaterial
                color={0x1a2a55}
                metalness={0.3}
                roughness={0.6}
              />
            </mesh>

            {/* Solar cell grid pattern */}
            {Array.from({ length: 5 }).map((_, j) =>
              Array.from({ length: 3 }).map((_, k) => (
                <mesh
                  key={`cell-${i}-${j}-${k}`}
                  position={[-0.24 + j * 0.12, dir * 0.012, -0.1 + k * 0.1]}
                >
                  <boxGeometry args={[0.1, 0.002, 0.08]} />
                  <meshStandardMaterial
                    color={0x0a1833}
                    metalness={0.4}
                    roughness={0.5}
                  />
                </mesh>
              ))
            )}

            {/* Panel frame */}
            <mesh position={[0, 0, 0]}>
              <boxGeometry args={[0.64, 0.025, 0.38]} />
              <meshStandardMaterial {...whiteMetal} />
            </mesh>
            <mesh position={[0, 0, 0]}>
              <boxGeometry args={[0.58, 0.03, 0.33]} />
              <meshStandardMaterial color={0x1a2a55} metalness={0.3} roughness={0.5} />
            </mesh>

            {/* Panel hinge/mount */}
            <mesh position={[0, -dir * 0.25, 0]}>
              <boxGeometry args={[0.08, 0.45, 0.04]} />
              <meshStandardMaterial {...darkMetal} />
            </mesh>

            {/* Power conduit lines */}
            {[-0.2, 0.2].map((x, m) => (
              <mesh key={`conduit-${i}-${m}`} position={[x, -dir * 0.12, 0.18]}>
                <cylinderGeometry args={[0.008, 0.008, 0.2, 6]} />
                <meshStandardMaterial {...copperHeat} />
              </mesh>
            ))}
          </group>
        ))}
      </group>

      {/* ========== THERMAL RADIATORS ========== */}
      <group>
        {/* Heat dissipation panels - essential for high-power optical system */}
        {[0.4, -0.4].map((z, i) => (
          <group key={`radiator-${i}`} position={[0, 0, z]} rotation={[0, i * Math.PI, 0]}>
            {/* Radiator fins - copper-colored */}
            {Array.from({ length: 5 }).map((_, j) => (
              <mesh key={`fin-${i}-${j}`} position={[-0.05 + j * 0.025, 0, 0]}>
                <boxGeometry args={[0.003, 0.3, 0.15]} />
                <meshStandardMaterial
                  {...copperHeat}
                  emissive={0x442211}
                  emissiveIntensity={0.3}
                />
              </mesh>
            ))}

            {/* Radiator base */}
            <mesh position={[0, 0, 0]}>
              <boxGeometry args={[0.12, 0.05, 0.04]} />
              <meshStandardMaterial {...darkMetal} />
            </mesh>

            {/* Heat pipes */}
            <mesh position={[0, 0.18, 0.05]} rotation={[Math.PI / 2, 0, 0]}>
              <cylinderGeometry args={[0.012, 0.012, 0.25, 6]} />
              <meshStandardMaterial {...copperHeat} />
            </mesh>
          </group>
        ))}
      </group>

      {/* ========== ANTENNA ARRAY ========== */}
      <group position={[0, 0.22, 0]}>
        {/* Communication dish - for telemetry and control */}
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[0.12, 0.08, 0.03, 16]} />
          <meshStandardMaterial {...whiteMetal} />
        </mesh>

        {/* Dish feed horn */}
        <mesh position={[0, 0.05, 0]}>
          <coneGeometry args={[0.02, 0.06, 8]} />
          <meshStandardMaterial {...goldMLI} />
        </mesh>

        {/* Support tripod */}
        {[0, 1, 2].map((i) => {
          const angle = (i / 3) * Math.PI * 2;
          return (
            <mesh
              key={`leg-${i}`}
              position={[Math.cos(angle) * 0.06, 0.025, Math.sin(angle) * 0.06]}
              rotation={[0.3, angle, 0]}
            >
              <cylinderGeometry args={[0.005, 0.005, 0.08, 4]} />
              <meshStandardMaterial {...darkMetal} />
            </mesh>
          );
        })}
      </group>

      {/* ========== STATUS LIGHTS ========== */}
      {[
        { pos: [0.22, 0.15, 0], color: 0x00ff88 },
        { pos: [-0.22, 0.15, 0], color: 0x00ff88 },
        { pos: [0, 0.18, 0.22], color: 0xffaa00 },
        { pos: [0, 0.18, -0.22], color: 0xffaa00 },
      ].map((light, i) => (
        <group key={`light-${i}`} position={light.pos as [number, number, number]}>
          <mesh>
            <sphereGeometry args={[0.015, 8, 8]} />
            <meshBasicMaterial color={light.color} toneMapped={false} />
          </mesh>
          <pointLight color={light.color} intensity={0.3} distance={0.3} />
        </group>
      ))}

      {/* ========== MAIN LIGHTS ========== */}
      {/* Primary mirror illumination */}
      <pointLight position={[0.55, 0, 0]} color={0xffdd88} intensity={2} distance={4} />

      {/* Secondary mirror glow */}
      <pointLight position={[-0.45, 0.1, 0]} color={0x88ffaa} intensity={1} distance={3} />

      {/* Hub ambient */}
      <pointLight position={[0, 0, 0]} color={0x88ccff} intensity={0.8} distance={2} />
    </group>
  );
}

// Spaceport - Dark side landing facility for mining transports
function Spaceport({ position, sunPosition }: { position: [number, number, number]; sunPosition: [number, number, number] }) {
  const spaceportRef = useRef<THREE.Group>(null);
  const beaconRef = useRef<THREE.Mesh>(null);

  // Calculate rotation to face outward (away from Mercury center)
  const rotation = useMemo(() => {
    const pos = new THREE.Vector3(...position);
    const normal = pos.clone().normalize();
    // Create quaternion to orient the spaceport to face outward
    const up = new THREE.Vector3(0, 1, 0);
    const quaternion = new THREE.Quaternion().setFromUnitVectors(up, normal);
    const euler = new THREE.Euler().setFromQuaternion(quaternion);
    return [euler.x, euler.y, euler.z] as [number, number, number];
  }, [position]);

  // Animate beacon lights
  useFrame((state) => {
    if (beaconRef.current) {
      const material = beaconRef.current.material as THREE.MeshBasicMaterial;
      material.opacity = 0.5 + Math.sin(state.clock.elapsedTime * 3) * 0.5;
    }
  });

  return (
    <group position={position} rotation={rotation}>
      {/* Main landing platform - hexagonal design */}
      <mesh position={[0, 0.02, 0]}>
        <cylinderGeometry args={[0.5, 0.55, 0.04, 6]} />
        <meshStandardMaterial color={0x445566} metalness={0.7} roughness={0.4} />
      </mesh>

      {/* Landing pad markings - concentric rings */}
      <mesh position={[0, 0.045, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <ringGeometry args={[0.15, 0.18, 24]} />
        <meshBasicMaterial color={0x00ffaa} toneMapped={false} transparent opacity={0.8} />
      </mesh>
      <mesh position={[0, 0.045, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <ringGeometry args={[0.3, 0.33, 24]} />
        <meshBasicMaterial color={0x00ffaa} toneMapped={false} transparent opacity={0.6} />
      </mesh>

      {/* Landing guidance lights - corner beacons */}
      {[0, Math.PI/3, Math.PI*2/3, Math.PI, Math.PI*4/3, Math.PI*5/3].map((angle, i) => (
        <group key={i} position={[Math.cos(angle) * 0.48, 0.08, Math.sin(angle) * 0.48]}>
          <mesh>
            <cylinderGeometry args={[0.03, 0.035, 0.08, 6]} />
            <meshStandardMaterial color={0x556677} metalness={0.6} roughness={0.4} />
          </mesh>
          <mesh ref={i === 0 ? beaconRef : undefined} position={[0, 0.06, 0]}>
            <sphereGeometry args={[0.025, 8, 8]} />
            <meshBasicMaterial color={0x00ff88} toneMapped={false} transparent opacity={0.8} />
          </mesh>
          <pointLight position={[0, 0.06, 0]} color={0x00ff88} intensity={0.3} distance={0.5} />
        </group>
      ))}

      {/* Control tower */}
      <group position={[0.35, 0.1, 0.2]}>
        {/* Tower base */}
        <mesh position={[0, 0.15, 0]}>
          <boxGeometry args={[0.12, 0.3, 0.12]} />
          <meshStandardMaterial color={0x556666} metalness={0.6} roughness={0.4} />
        </mesh>
        {/* Control room */}
        <mesh position={[0, 0.35, 0]}>
          <boxGeometry args={[0.15, 0.1, 0.15]} />
          <meshStandardMaterial color={0x667777} metalness={0.5} roughness={0.4} />
        </mesh>
        {/* Windows */}
        {[0, Math.PI/2, Math.PI, Math.PI*3/2].map((angle, i) => (
          <mesh key={i} position={[Math.cos(angle) * 0.076, 0.35, Math.sin(angle) * 0.076]}>
            <boxGeometry args={[0.04, 0.06, 0.01]} />
            <meshBasicMaterial color={0x88ffff} transparent opacity={0.7} toneMapped={false} />
          </mesh>
        ))}
        {/* Radar antenna */}
        <mesh position={[0, 0.45, 0]}>
          <cylinderGeometry args={[0.01, 0.015, 0.1, 6]} />
          <meshStandardMaterial color={0x888899} metalness={0.8} roughness={0.2} />
        </mesh>
        <mesh position={[0, 0.52, 0]}>
          <sphereGeometry args={[0.02, 8, 8]} />
          <meshBasicMaterial color={0xff4400} toneMapped={false} />
        </mesh>
      </group>

      {/* Fuel depot / refueling station */}
      <group position={[-0.35, 0.05, 0.15]}>
        {/* Fuel tanks */}
        {[-0.08, 0.08].map((x, i) => (
          <group key={i} position={[x, 0.12, 0]}>
            <mesh>
              <cylinderGeometry args={[0.06, 0.06, 0.2, 12]} />
              <meshStandardMaterial
                color={i === 0 ? 0x4466aa : 0x44aa66}
                metalness={0.6}
                roughness={0.4}
              />
            </mesh>
            {/* Tank supports */}
            <mesh position={[0, -0.12, 0]}>
              <boxGeometry args={[0.08, 0.04, 0.08]} />
              <meshStandardMaterial color={0x555555} metalness={0.5} roughness={0.6} />
            </mesh>
          </group>
        ))}
        {/* Fuel lines */}
        <mesh position={[0, 0.05, 0.08]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.015, 0.015, 0.18, 6]} />
          <meshStandardMaterial color={0x333333} roughness={0.8} />
        </mesh>
        {/* Pump housing */}
        <mesh position={[0, 0.04, 0.12]}>
          <boxGeometry args={[0.08, 0.08, 0.06]} />
          <meshStandardMaterial color={0x556655} metalness={0.5} roughness={0.5} />
        </mesh>
      </group>

      {/* Cargo handling crane */}
      <group position={[-0.2, 0.05, -0.3]}>
        {/* Crane base */}
        <mesh position={[0, 0.06, 0]}>
          <cylinderGeometry args={[0.08, 0.1, 0.1, 8]} />
          <meshStandardMaterial color={0x666655} metalness={0.6} roughness={0.4} />
        </mesh>
        {/* Vertical arm */}
        <mesh position={[0, 0.25, 0]}>
          <boxGeometry args={[0.06, 0.35, 0.06]} />
          <meshStandardMaterial color={0x887744} metalness={0.5} roughness={0.5} />
        </mesh>
        {/* Horizontal boom */}
        <mesh position={[0.12, 0.4, 0]} rotation={[0, 0, Math.PI / 2]}>
          <boxGeometry args={[0.04, 0.28, 0.04]} />
          <meshStandardMaterial color={0x887744} metalness={0.5} roughness={0.5} />
        </mesh>
        {/* Cable */}
        <mesh position={[0.22, 0.3, 0]}>
          <cylinderGeometry args={[0.005, 0.005, 0.2, 4]} />
          <meshStandardMaterial color={0x333333} roughness={0.9} />
        </mesh>
        {/* Hook */}
        <mesh position={[0.22, 0.18, 0]}>
          <torusGeometry args={[0.025, 0.008, 6, 12, Math.PI]} />
          <meshStandardMaterial color={0x888888} metalness={0.8} roughness={0.2} />
        </mesh>
      </group>

      {/* Cargo containers awaiting pickup */}
      {[[0.15, 0.08, -0.25], [0.25, 0.06, -0.15]].map(([x, y, z], i) => (
        <mesh key={i} position={[x, y, z]}>
          <boxGeometry args={[0.08, 0.08, 0.12]} />
          <meshStandardMaterial
            color={i === 0 ? 0x886633 : 0x996644}
            metalness={0.4}
            roughness={0.6}
          />
        </mesh>
      ))}

      {/* Approach beacon - tall marker */}
      <group position={[0.4, 0.1, -0.35]}>
        <mesh position={[0, 0.25, 0]}>
          <cylinderGeometry args={[0.015, 0.02, 0.5, 6]} />
          <meshStandardMaterial color={0x888888} metalness={0.7} roughness={0.3} />
        </mesh>
        {/* Strobe light at top */}
        <mesh position={[0, 0.52, 0]}>
          <sphereGeometry args={[0.03, 8, 8]} />
          <meshBasicMaterial color={0xff0000} toneMapped={false} />
        </mesh>
        <pointLight position={[0, 0.52, 0]} color={0xff0000} intensity={0.5} distance={1.5} />
      </group>

      {/* Pad lighting for visibility */}
      <pointLight position={[0, 0.3, 0]} color={0xaaffaa} intensity={0.5} distance={1.5} />
    </group>
  );
}

// Polar Starship Base - Landing pad for Starships from Earth at Mercury's north pole
function PolarStarshipBase({ position }: { position: [number, number, number] }) {
  const baseRef = useRef<THREE.Group>(null);
  const beaconRefs = useRef<THREE.Mesh[]>([]);

  // Calculate rotation to face outward (radially from planet center)
  const rotation = useMemo(() => {
    const pos = new THREE.Vector3(...position);
    const normal = pos.clone().normalize();
    const up = new THREE.Vector3(0, 1, 0);
    const quaternion = new THREE.Quaternion().setFromUnitVectors(up, normal);
    const euler = new THREE.Euler().setFromQuaternion(quaternion);
    return [euler.x, euler.y, euler.z] as [number, number, number];
  }, [position]);

  // Animate landing beacons
  useFrame((state) => {
    beaconRefs.current.forEach((beacon, i) => {
      if (beacon) {
        const material = beacon.material as THREE.MeshBasicMaterial;
        const phase = state.clock.elapsedTime * 2 + i * 0.5;
        material.opacity = 0.4 + Math.sin(phase) * 0.4;
      }
    });
  });

  return (
    <group position={position} rotation={rotation}>
      {/* === MAIN LANDING PLATFORM === */}
      {/* Large circular pad with SpaceX-style markings */}
      <mesh position={[0, 0.02, 0]}>
        <cylinderGeometry args={[0.8, 0.85, 0.04, 24]} />
        <meshStandardMaterial color={0x3a3a40} metalness={0.6} roughness={0.5} />
      </mesh>

      {/* Central X marking - SpaceX style */}
      <group position={[0, 0.045, 0]} rotation={[Math.PI / 2, 0, 0]}>
        {/* X arms */}
        {[Math.PI / 4, -Math.PI / 4].map((angle, i) => (
          <mesh key={i} rotation={[0, 0, angle]}>
            <boxGeometry args={[0.08, 0.6, 0.001]} />
            <meshBasicMaterial color={0xffffff} toneMapped={false} />
          </mesh>
        ))}
        {/* Circle around X */}
        <mesh>
          <ringGeometry args={[0.25, 0.28, 24]} />
          <meshBasicMaterial color={0xffffff} toneMapped={false} transparent opacity={0.9} />
        </mesh>
      </group>

      {/* Outer landing guidance ring */}
      <mesh position={[0, 0.045, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <ringGeometry args={[0.55, 0.58, 24]} />
        <meshBasicMaterial color={0x44ff88} toneMapped={false} transparent opacity={0.7} />
      </mesh>

      {/* === LANDING LEGS CATCH SYSTEM (Mechazilla-style) === */}
      {/* Tower structure with catch arms */}
      <group position={[0.65, 0.1, 0]}>
        {/* Main tower */}
        <mesh position={[0, 0.5, 0]}>
          <boxGeometry args={[0.12, 1.0, 0.12]} />
          <meshStandardMaterial color={0x556677} metalness={0.7} roughness={0.3} />
        </mesh>

        {/* Tower lattice details */}
        {[0.2, 0.5, 0.8].map((y, i) => (
          <mesh key={i} position={[0, y, 0]}>
            <boxGeometry args={[0.18, 0.03, 0.18]} />
            <meshStandardMaterial color={0x445566} metalness={0.6} roughness={0.4} />
          </mesh>
        ))}

        {/* Catch arms (chopsticks) */}
        {[-0.15, 0.15].map((z, i) => (
          <group key={i} position={[-0.15, 0.75, z]}>
            <mesh rotation={[0, 0, -Math.PI / 6]}>
              <boxGeometry args={[0.04, 0.5, 0.06]} />
              <meshStandardMaterial color={0x667788} metalness={0.8} roughness={0.2} />
            </mesh>
            {/* Arm padding */}
            <mesh position={[-0.18, 0.08, 0]}>
              <boxGeometry args={[0.08, 0.15, 0.08]} />
              <meshStandardMaterial color={0x888888} roughness={0.6} />
            </mesh>
          </group>
        ))}

        {/* Tower top with crane */}
        <mesh position={[0, 1.05, 0]}>
          <boxGeometry args={[0.15, 0.1, 0.15]} />
          <meshStandardMaterial color={0x667788} metalness={0.7} roughness={0.3} />
        </mesh>

        {/* Warning lights */}
        <mesh position={[0, 1.12, 0]}>
          <sphereGeometry args={[0.025, 8, 8]} />
          <meshBasicMaterial color={0xff4400} toneMapped={false} />
        </mesh>
        <pointLight position={[0, 1.12, 0]} color={0xff4400} intensity={0.5} distance={1} />
      </group>

      {/* === SUPPORT BUILDINGS === */}

      {/* Control center / mission control */}
      <group position={[-0.5, 0.05, 0.3]}>
        <mesh position={[0, 0.12, 0]}>
          <boxGeometry args={[0.25, 0.2, 0.18]} />
          <meshStandardMaterial color={0x556666} metalness={0.5} roughness={0.5} />
        </mesh>
        {/* Large windows */}
        <mesh position={[0.125, 0.14, 0]}>
          <boxGeometry args={[0.01, 0.12, 0.12]} />
          <meshBasicMaterial color={0x88ccff} transparent opacity={0.8} toneMapped={false} />
        </mesh>
        {/* Roof */}
        <mesh position={[0, 0.24, 0]}>
          <boxGeometry args={[0.27, 0.03, 0.2]} />
          <meshStandardMaterial color={0x667777} metalness={0.6} roughness={0.4} />
        </mesh>
        {/* Antenna */}
        <mesh position={[0.1, 0.32, 0]}>
          <cylinderGeometry args={[0.008, 0.01, 0.15, 6]} />
          <meshStandardMaterial color={0x888899} metalness={0.8} roughness={0.2} />
        </mesh>
      </group>

      {/* Propellant storage tanks */}
      <group position={[-0.4, 0.08, -0.35]}>
        {/* LOX tank (white/silver) */}
        <group position={[-0.1, 0.15, 0]}>
          <mesh>
            <cylinderGeometry args={[0.1, 0.1, 0.3, 12]} />
            <meshStandardMaterial color={0xdddddd} metalness={0.7} roughness={0.3} />
          </mesh>
          <mesh position={[0, 0.18, 0]}>
            <sphereGeometry args={[0.1, 12, 12, 0, Math.PI * 2, 0, Math.PI / 2]} />
            <meshStandardMaterial color={0xdddddd} metalness={0.7} roughness={0.3} />
          </mesh>
          {/* Tank label */}
          <mesh position={[0.101, 0, 0]}>
            <boxGeometry args={[0.001, 0.08, 0.04]} />
            <meshBasicMaterial color={0x0066ff} />
          </mesh>
        </group>

        {/* CH4 tank (darker) */}
        <group position={[0.1, 0.15, 0]}>
          <mesh>
            <cylinderGeometry args={[0.1, 0.1, 0.3, 12]} />
            <meshStandardMaterial color={0xaaaaaa} metalness={0.6} roughness={0.4} />
          </mesh>
          <mesh position={[0, 0.18, 0]}>
            <sphereGeometry args={[0.1, 12, 12, 0, Math.PI * 2, 0, Math.PI / 2]} />
            <meshStandardMaterial color={0xaaaaaa} metalness={0.6} roughness={0.4} />
          </mesh>
        </group>

        {/* Connecting pipes */}
        <mesh position={[0, 0, 0.12]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.015, 0.015, 0.25, 6]} />
          <meshStandardMaterial color={0x444444} roughness={0.7} />
        </mesh>
      </group>

      {/* === LANDING BEACONS === */}
      {[0, Math.PI / 2, Math.PI, (Math.PI * 3) / 2].map((angle, i) => (
        <group
          key={i}
          position={[Math.cos(angle) * 0.72, 0.08, Math.sin(angle) * 0.72]}
        >
          <mesh>
            <cylinderGeometry args={[0.025, 0.03, 0.1, 6]} />
            <meshStandardMaterial color={0x445566} metalness={0.6} roughness={0.4} />
          </mesh>
          <mesh
            ref={(el) => { if (el) beaconRefs.current[i] = el; }}
            position={[0, 0.07, 0]}
          >
            <sphereGeometry args={[0.03, 8, 8]} />
            <meshBasicMaterial color={0x00ff66} toneMapped={false} transparent opacity={0.8} />
          </mesh>
          <pointLight position={[0, 0.07, 0]} color={0x00ff66} intensity={0.4} distance={0.8} />
        </group>
      ))}

      {/* === SERVICE VEHICLES === */}
      {/* Small rover / transport */}
      <group position={[0.25, 0.05, 0.45]}>
        <mesh position={[0, 0.03, 0]}>
          <boxGeometry args={[0.08, 0.04, 0.05]} />
          <meshStandardMaterial color={0x888877} metalness={0.5} roughness={0.5} />
        </mesh>
        {/* Wheels */}
        {[
          [-0.035, -0.025],
          [0.035, -0.025],
          [-0.035, 0.025],
          [0.035, 0.025],
        ].map(([x, z], i) => (
          <mesh key={i} position={[x, 0.01, z]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.015, 0.015, 0.01, 8]} />
            <meshStandardMaterial color={0x333333} roughness={0.9} />
          </mesh>
        ))}
      </group>

      {/* Pad floodlights */}
      <pointLight position={[0, 0.5, 0]} color={0xffffee} intensity={0.8} distance={2} />
      <pointLight position={[0.4, 0.3, 0.4]} color={0xffeecc} intensity={0.4} distance={1.5} />
    </group>
  );
}

function createMercuryLabel(): THREE.Texture {
  const canvas = document.createElement('canvas');
  canvas.width = 384;
  canvas.height = 96;
  const ctx = canvas.getContext('2d')!;

  ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
  ctx.fillRect(0, 0, 384, 96);

  ctx.strokeStyle = '#ff8844';
  ctx.lineWidth = 2;
  ctx.strokeRect(2, 2, 380, 92);

  ctx.fillStyle = '#ff8844';
  ctx.font = 'bold 22px monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('Mercury ISRU Hub', 192, 36);

  ctx.font = '14px monospace';
  ctx.fillStyle = '#cc6633';
  ctx.fillText('@ 0.4 AU | Optical Relay', 192, 62);

  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  return texture;
}
